import Divider from 'LuxKit/Divider'
import HeadData from 'components/Common/HeadData'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import GeoContext from 'contexts/geoContext'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { useLoyaltyCreditCardForcedLogin } from 'loyalty/hooks/useLoyaltyCreditCardForcedLogin'
import { useLoyaltyCreditCardFeatureFlag } from 'loyalty/hooks/useLoyaltyCreditCardFeatureFlag'
import { rem } from 'polished'
import { useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import LoyaltyCreditCardHeroSection from './LoyaltyCreditCardHeroSection'
import LoyaltyCreditCardEarnSection from './LoyaltyCreditCardEarnSection'
import LoyaltyCreditCardHighlightsSection from './LoyaltyCreditCardHighlightsSection'
import LoyaltyCreditCardTravelBenefitsSection from './LoyaltyCreditCardTravelBenefitsSection'
import LoyaltyCreditCardMaldivesSection from './LoyaltyCreditCardMaldivesSection'
import LoyaltyCreditCardApplePaySection from './LoyaltyCreditCardApplePaySection'
import LoyaltyCreditCardApplySection from './LoyaltyCreditCardApplySection'
import LoyaltyCreditCardFaqSection from './LoyaltyCreditCardFaqSection'
import { LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS, LOYALTY_CREDIT_CARD_SHARE_IMAGE_ID } from './loyaltyCreditCardConstants'

const PageWrapper = styled.div`
  && .${LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS} {
    padding-inline-start: ${rem(20)};
    padding-inline-end: ${rem(20)};
  }
`

function LoyaltyCreditCardPage() {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const { hasResolvedFlag, isEnabled, requiresLogin } = useLoyaltyCreditCardFeatureFlag()
  const loyaltyLandingPagePath = `/${currentRegionCode.toLowerCase()}/${LOYALTY_LANDING_PAGE}`
  const isAwaitingDecision = useLoyaltyCreditCardForcedLogin(requiresLogin)

  // Nothing of the page renders behind the forced login modal: while the
  // campaign is staff-only, its content is not for whoever happens to open
  // the link. The wait also covers the gap after a forced login, so the
  // anonymous decision cannot redirect out from under the fresh one.
  if (!hasResolvedFlag || requiresLogin || isAwaitingDecision) {
    return <LoadingIndicator />
  }

  if (!isEnabled) {
    return <RedirectWithStatus code={302} to={loyaltyLandingPagePath} />
  }

  return (
    <PageWrapper>
      <HeadData
        title={intl.formatMessage({
          id: '4o2L4QuIYLqU__MU7Hc4',
          defaultMessage: 'Luxury Escapes - Introducing the NEW Société Credit Card',
          description: 'Page title used for the browser tab and for the link preview when the Société Credit Card landing page is shared',
        })}
        description={intl.formatMessage({
          id: 'jXiM6frmAaFaWR_fGQB_',
          defaultMessage: 'Earn Société Points faster with everyday spending.',
          description: 'Page description used for the link preview when the Société Credit Card landing page is shared on social media',
        })}
        imageId={LOYALTY_CREDIT_CARD_SHARE_IMAGE_ID}
        imageAlt={intl.formatMessage({
          id: 'VsUvmjmlX0vfe2cAwjQ3',
          defaultMessage: 'A Luxury Escapes Société Credit Card resting in beach sand',
          description: 'Alternative text for the link preview image shown when the Société Credit Card landing page is shared',
        })}
      />
      <LoyaltyCreditCardHeroSection />
      <LoyaltyCreditCardEarnSection />
      <Divider kind="primary" />
      <LoyaltyCreditCardHighlightsSection />
      <Divider kind="primary" />
      <LoyaltyCreditCardTravelBenefitsSection />
      <LoyaltyCreditCardMaldivesSection />
      <LoyaltyCreditCardApplePaySection />
      <LoyaltyCreditCardApplySection />
      <LoyaltyCreditCardFaqSection />
    </PageWrapper>
  )
}

export default LoyaltyCreditCardPage
